export interface ScopedUiEntityFetchOptions {
  signal?: AbortSignal;
}

export interface ScopedUiEntityResource<TRecord = unknown> {
  fetch(
    options: ScopedUiEntityFetchOptions,
  ): Promise<readonly TRecord[]>;
  hydrate(scopeKey: string): Promise<void>;
  replace(
    scopeKey: string,
    records: readonly TRecord[],
  ): Promise<void>;
}

interface ScopedUiEntityLoadParams {
  resources: readonly ScopedUiEntityResource[];
  scopeKey: string;
  shouldIgnore: () => boolean;
  signal: AbortSignal;
}

export async function loadScopedUiEntities({
  resources,
  scopeKey,
  shouldIgnore,
  signal,
}: ScopedUiEntityLoadParams): Promise<void> {
  await Promise.all(resources.map((resource) => resource.hydrate(scopeKey)));
  if (shouldStopLoading(signal, shouldIgnore)) return;

  const results = await Promise.allSettled(resources.map(async (resource) => {
    const records = await resource.fetch({ signal });
    if (shouldStopLoading(signal, shouldIgnore)) return;

    await resource.replace(scopeKey, records);
  }));
  const failures = results.filter((result) => result.status === "rejected");
  if (failures.length === results.length && failures[0]) {
    throw failures[0].reason;
  }
}

function shouldStopLoading(
  signal: AbortSignal,
  shouldIgnore: () => boolean,
): boolean {
  return signal.aborted || shouldIgnore();
}
